/** Mensagens/rejeições que poluem Tipo=Erro e não servem para digest. */

const RESIZE_OBSERVER_RE =
  /ResizeObserver loop (completed with undelivered notifications|limit exceeded)/i;
const SCRIPT_ERROR_RE = /^Script error\.?$/i;
const OBJECT_OBJECT_RE = /^\[object Object\]$/i;
const ABORT_RE =
  /^(Aborted|The user aborted a request|AbortError: Aborted)$/i;
const ABORT_NAME_RE = /^AbortError$/i;
const UNAUTHORIZED_401_RE =
  /Unauthorized|"status"\s*:\s*401|"statusCode"\s*:\s*401/i;
const NETWORK_FETCH_RE =
  /Failed to fetch|Load failed|FETCH_ERROR|FetchEvent\.respondWith|NetworkError when attempting to fetch/i;
const WEBVIEW_IAB_RE =
  /Java object is gone|iabjs:\/\/|Error invoking postMessage/i;

export function isErrorLogNoise(message: unknown): boolean {
  const text = normalizeMessage(message);
  if (!text) return false;
  return (
    RESIZE_OBSERVER_RE.test(text) ||
    SCRIPT_ERROR_RE.test(text) ||
    OBJECT_OBJECT_RE.test(text) ||
    ABORT_RE.test(text) ||
    ABORT_NAME_RE.test(text) ||
    UNAUTHORIZED_401_RE.test(text) ||
    NETWORK_FETCH_RE.test(text) ||
    WEBVIEW_IAB_RE.test(text)
  );
}

/**
 * Filtra unhandledrejection estruturado (RTK Query, AbortError, etc.)
 * antes de serializar / postLog.
 */
export function isErrorLogNoiseReason(reason: unknown): boolean {
  if (reason == null) return false;

  if (reason instanceof Error) {
    if (reason.name === "AbortError" || ABORT_NAME_RE.test(reason.name)) {
      return true;
    }
    if (isErrorLogNoise(reason.message) || isErrorLogNoise(reason.name)) {
      return true;
    }
  }

  if (typeof reason === "object") {
    if (hasUnauthorized401(reason) || isNetworkFetchError(reason)) {
      return true;
    }
  }

  const serialized = serializeUnknownError(reason);
  if (
    serialized.name === "AbortError" ||
    ABORT_NAME_RE.test(serialized.name ?? "")
  ) {
    return true;
  }
  return isErrorLogNoise(serialized.message) || isErrorLogNoise(serialized.name);
}

export function serializeUnknownError(reason: unknown): {
  name?: string;
  message: string;
  stack?: string;
  status?: number | string;
  apiMessage?: string;
} {
  if (reason instanceof Error) {
    return {
      name: reason.name,
      message: reason.message ?? "",
      stack: reason.stack,
    };
  }

  if (reason !== null && typeof reason === "object") {
    const rec = reason as Record<string, unknown>;
    const nested =
      rec.error instanceof Error
        ? rec.error
        : typeof rec.error === "object" && rec.error !== null
          ? (rec.error as Record<string, unknown>)
          : null;

    const status =
      (typeof rec.status === "number" || typeof rec.status === "string"
        ? rec.status
        : undefined) ??
      (nested &&
      !(nested instanceof Error) &&
      (typeof nested.status === "number" || typeof nested.status === "string")
        ? (nested.status as number | string)
        : undefined);

    const apiMessage =
      extractApiMessage(rec) ??
      (nested && !(nested instanceof Error) ? extractApiMessage(nested) : undefined);

    const messageCandidate =
      pickString(rec.message) ??
      pickString(rec.Message) ??
      apiMessage ??
      (nested instanceof Error
        ? nested.message
        : nested
          ? pickString(nested.message) ?? pickString(nested.Message)
          : undefined) ??
      (typeof rec.error === "string" ? rec.error : undefined);

    const nameCandidate =
      pickString(rec.name) ??
      pickString(rec.Name) ??
      (nested instanceof Error
        ? nested.name
        : nested
          ? pickString(nested.name) ?? pickString(nested.Name)
          : undefined) ??
      (status != null ? `HttpError` : undefined);

    const stackCandidate =
      pickString(rec.stack) ??
      (nested instanceof Error
        ? nested.stack
        : nested
          ? pickString(nested.stack)
          : undefined);

    if (messageCandidate || nameCandidate || status != null || apiMessage) {
      const message =
        messageCandidate ??
        apiMessage ??
        (status != null ? `HTTP ${status}` : nameCandidate ?? "");
      return {
        name: nameCandidate ?? "Error",
        message,
        stack: stackCandidate,
        ...(status != null ? { status } : {}),
        ...(apiMessage ? { apiMessage } : {}),
      };
    }

    try {
      return {
        name: "UnhandledRejection",
        message: JSON.stringify(reason).slice(0, 2000),
      };
    } catch {
      return { name: "UnhandledRejection", message: "[unserializable]" };
    }
  }

  return {
    name: "UnhandledRejection",
    message: reason === undefined || reason === null ? "" : String(reason),
  };
}

function extractApiMessage(rec: Record<string, unknown>): string | undefined {
  const data = rec.data;
  if (data && typeof data === "object") {
    const d = data as Record<string, unknown>;
    return pickString(d.message) ?? pickString(d.Message);
  }
  return undefined;
}

function hasUnauthorized401(value: unknown, depth = 0): boolean {
  if (depth > 4 || value == null) return false;
  if (typeof value !== "object") return false;
  const rec = value as Record<string, unknown>;

  if (rec.status === 401 || rec.statusCode === 401) return true;

  const data = rec.data;
  if (data && typeof data === "object") {
    const d = data as Record<string, unknown>;
    if (d.statusCode === 401 || d.status === 401) return true;
    const msg = pickString(d.message) ?? pickString(d.Message);
    if (msg && /^Unauthorized$/i.test(msg) && (d.statusCode === 401 || d.status === 401 || rec.status === 401)) {
      return true;
    }
  }

  if (rec.error != null && hasUnauthorized401(rec.error, depth + 1)) return true;

  try {
    return UNAUTHORIZED_401_RE.test(JSON.stringify(value).slice(0, 2000));
  } catch {
    return false;
  }
}

function isNetworkFetchError(value: unknown, depth = 0): boolean {
  if (depth > 4 || value == null) return false;
  if (typeof value !== "object") return false;
  const rec = value as Record<string, unknown>;

  const status = rec.status;
  const errText =
    pickString(rec.error) ??
    (typeof rec.error === "object" && rec.error
      ? pickString((rec.error as Record<string, unknown>).message) ??
        pickString((rec.error as Record<string, unknown>).Message) ??
        (typeof (rec.error as Record<string, unknown>).error === "string"
          ? String((rec.error as Record<string, unknown>).error)
          : undefined)
      : undefined) ??
    pickString(rec.message);

  if (status === "FETCH_ERROR") {
    if (!errText || NETWORK_FETCH_RE.test(errText)) return true;
  }

  if (errText && NETWORK_FETCH_RE.test(errText)) return true;

  if (rec.error != null && typeof rec.error === "object") {
    if (isNetworkFetchError(rec.error, depth + 1)) return true;
  }

  try {
    const blob = JSON.stringify(value).slice(0, 2000);
    return (
      /"status"\s*:\s*"FETCH_ERROR"/i.test(blob) && NETWORK_FETCH_RE.test(blob)
    );
  } catch {
    return false;
  }
}

function normalizeMessage(message: unknown): string {
  if (typeof message === "string") return message.trim();
  if (message == null) return "";
  return String(message).trim();
}

function pickString(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}
